import { useCallback, useMemo } from 'react';

import type { CreatePaginationOptions, CreatePaginationUseEntities } from './createPaginationTypes';
import { createPaginationEntity } from '../paginationEntity/createPaginationEntity';
import type {
    CustomQueryPageParams,
    ListHelpers,
    QueryParamsBaseType,
} from '../paginationEntity/createPaginationEntityTypes';
import { KeyNamespaces } from '../utils/getKey';

/**
 * Creates a custom hook and helper functions for managing paginated data.
 *
 * @template Entity - The type of entity being paginated.
 * @template QueryPageParams - The type of query parameters for pagination (default: Record<string, any>).
 * @template AdditionalData - The type of any additional data returned by the query (default: unknown).
 *
 * @param {CreatePaginationOptions<Entity, QueryPageParams, AdditionalData>} opts - Configuration options.
 * @param {string} opts.name - A unique name for the pagination entity.
 * @param {function} opts.payloadCreator - Function to fetch paginated data.
 * @param {function} [opts.selectId] - Function to select the ID from an entity.
 * @param {number} [opts.cacheDuration] - Duration in milliseconds for caching.
 *
 * @returns {[CreatePaginationUseEntities<Entity, QueryPageParams, AdditionalData>, ListHelpers<Entity, QueryPageParams>]}
 * A tuple containing:
 *   1. A custom hook for using the paginated entities.
 *   2. An object with helper functions for managing the paginated data.
 *
 * @example
 * const [useCardStatements, cardStatementsHelpers] = createPagination<Statement, { accountId: string }>({
 *   name: 'card/statements',
 *   payloadCreator: async (params, { pageNumber, pageSize }, signal) => {
 *     const response = await request<Response>(
 *       `${getEnv().prestoUrl}netbanking/my/${params.accountId}/statements?sort=statementDate&order=DESC&size=${pageSize}&page=${pageNumber}`,
 *       { signal }
 *     );
 *     return {
 *       pageNumber: response.pageNumber,
 *       pageCount: response.pageCount,
 *       data: response.statements,
 *     };
 *   },
 * });
 *
 * // In a component
 * const { data, fetchNextPage, isPending } = useCardStatements({ accountId: 'someId' });
 *
 * // Using helpers
 * cardStatementsHelpers.invalidateList({ accountId: 'someId' });
 *
 * @remarks
 * - This function is built on top of `createPaginationEntity` and provides a simpler API for pagination-only scenarios.
 * - The returned hook (`useEntities`) provides state and methods for fetching and managing paginated data.
 * - The helper functions allow for operations like invalidation, refetching, and cache updates outside of React components.
 * - This function does not support querying individual entities by ID or CRUD operations. For those features, use `createPaginationEntity` directly.
 */
export const createPagination = <
    Entity,
    QueryPageParams extends QueryParamsBaseType = Record<string, any>,
    AdditionalData = unknown,
>(
    opts: CreatePaginationOptions<Entity, QueryPageParams, AdditionalData>,
): [
    CreatePaginationUseEntities<Entity, QueryPageParams, AdditionalData>,
    ListHelpers<Entity, QueryPageParams>,
] => {
    const entity = createPaginationEntity({
        name: `${KeyNamespaces.PAGINATION}/${opts.name}`,
        selectId: opts.selectId,
        cacheDuration: opts.cacheDuration,
        payloadCreators: {
            queryPage: (customQueryPageParams, options) => {
                const queryParams = {
                    ...customQueryPageParams,
                };
                delete queryParams.pageNumber;
                delete queryParams.pageSize;

                return opts.payloadCreator(
                    queryParams as QueryPageParams,
                    {
                        pageNumber: customQueryPageParams?.pageNumber || 0,
                        pageSize: customQueryPageParams.pageSize,
                    },
                    options.signal,
                );
            },
            queryById: () => Promise.reject(new Error('Unsupported operation')),
        },
        skipEntityCacheSync: true,
    });

    const useEntities: CreatePaginationUseEntities<Entity, QueryPageParams, AdditionalData> = (
        useEntitiesArgs,
        useEntitiesOpts,
    ) => {
        const useEntitiesResult = entity.useEntities(useEntitiesArgs, useEntitiesOpts);
        const invalidate = useCallback(
            () =>
                entity.entityHelpers.invalidateList(
                    useEntitiesArgs || ({} as CustomQueryPageParams<QueryPageParams>),
                ),
            [JSON.stringify(useEntitiesArgs)],
        );
        return useMemo(
            () => ({
                ...useEntitiesResult,
                nextPageParams:
                    useEntitiesResult.nextPageParams as CustomQueryPageParams<QueryPageParams>,
                data: useEntitiesResult.data,
                invalidate,
                invalidateAllCacheKeys: entity.entityHelpers.invalidateAll,
            }),
            [useEntitiesResult, invalidate],
        );
    };

    return [
        useEntities,
        {
            invalidateList: entity.entityHelpers.invalidateList,
            invalidate: entity.entityHelpers.invalidate,
            invalidateAll: entity.entityHelpers.invalidateAll,
            queryListIfNeeded: entity.entityHelpers.queryListIfNeeded,
            refetchList: entity.entityHelpers.refetchList,
            selectEntitiesState: entity.entityHelpers.selectEntitiesState,
            updateCache: entity.entityHelpers.updateCache,
            selectEntityState: entity.entityHelpers.selectEntityState,
            invalidateLists: entity.entityHelpers.invalidateLists,
            getListSliceId: entity.entityHelpers.getListSliceId,
            getDetailSliceId: entity.entityHelpers.getDetailSliceId,
            updateListCache: entity.entityHelpers.updateListCache,
        },
    ];
};
